import React, { useCallback, useContext, useMemo, useState } from "react";
import { Card, CardContent, Typography } from "@mui/material";

import TableServer from "@/components/tables/TableServer";
import useFetcher from "@/helpers/hooks/useFetcher";
import { stripeMicroService } from "@/shared/services/stripeMicroService";
import { GlobalContext } from "@/helpers/contexts/contexts";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import { getFormatDate, keyToName } from "@/helpers/utils";
import { CurrencyFormateText } from "@/components/CurrencyFormat";

type FetchDataArgs = {
  pageIndex: number;
  pageSize: number;
  searchKey?: string;
  sortBy?: string;
  sortByDir?: string;
};

type TableCellProps = {
  row: {
    original: Record<string, any>;
  };
};

function InvoiceHistory() {
  const { loggedInUser, selectedBrand } = useContext(GlobalContext);
  const [filter, setFilters] = useState({
    page: 0,
    row: 10,
    sortBy: "",
    sortByDir: "",
    user_id: loggedInUser.id,
  });

  const reqData = useMemo(() => {
    return {
      ...filter,
      page: filter.page ? filter.page + 1 : 1,
      list_type: "invoice_history",
      brandId: selectedBrand?.id,
    };
  }, [filter, selectedBrand?.id]);

  const { data, isLoading, isValidating } = useFetcher(
    reqData,
    stripeMicroService.getInvoiceHistory,
    { revalidateOnFocus: false }
  );

  const pageCount = data?.data?.result?.data?.pageCount || 0;
  const totalRecords = data?.data?.result?.data?.recordsTotal || 0;

  const statusMapping = useMemo(
    () => ({
      unpaid: { label: "UnPaid", status: "warn" },
      pending: { label: "Pending", status: "info" },
      cancel: { label: "Cancel", status: "error" },
      paid: { label: "Paid", status: "success" },
    }),
    []
  );

  const columns = useMemo(
    () => [
      {
        Header: "Location",
        accessor: "location_name",
        width: 400,
      },
      {
        Header: "Location ID",
        accessor: "system_generated_location_id",
      },
      {
        Header: "Plan",
        accessor: "title",
        width: 400,
        Cell: ({ row }: TableCellProps) => {
          const {
            original: { title },
          } = row;
          return title || "N/A";
        },
      },

      {
        Header: "Date",
        accessor: "invoice_date",
        width: 250,
        Cell: ({ row }: TableCellProps) => {
          const {
            original: { invoice_date },
          } = row;
          return getFormatDate(invoice_date) || "N/A";
        },
      },
      {
        Header: "Amount",
        accessor: "amount_total",
        width: 300,
        Cell: ({ row }: TableCellProps) => {
          const {
            original: { amount_total, currency },
          } = row;
          const currencySuffix =
            String(currency || "").toLowerCase() === "cad" ? " CAD" : "";

          return (
            <CurrencyFormateText value={amount_total} suffix={currencySuffix} />
          );
        },
      },
      {
        Header: "Status",
        accessor: "payment_status",
        width: 190,
        Cell: ({ row }: TableCellProps) => {
          const {
            original: { payment_status },
          } = row;

          switch (payment_status) {
            case "unpaid":
              return (
                <StatusChip label={keyToName(payment_status)} status={"warn"} />
              );
            case "past_due":
            case "cancel":
              return (
                <StatusChip
                  label={keyToName(payment_status)}
                  status={"error"}
                />
              );
            case "pending":
              return (
                <StatusChip label={keyToName(payment_status)} status={"info"} />
              );
            case "paid":
              return (
                <StatusChip
                  label={keyToName(payment_status)}
                  status={"success"}
                />
              );
            default:
              return "-";
          }
        },
      },
    ],
    [statusMapping]
  );

  const dataList = useMemo(() => {
    return data?.data?.result?.data?.invoices || [];
  }, [data]);

  const handleFetchData = useCallback(
    ({ pageIndex, pageSize, searchKey, sortBy, sortByDir }: FetchDataArgs) => {
      setFilters((p) => ({
        ...p,
        page: pageIndex,
        row: pageSize,
        sortBy: sortBy,
        sortByDir: sortByDir,
        search: searchKey,
        user_id: loggedInUser.id,
      }));
    },
    []
  );

  return (
    <Card className="w-100 mb-3 border-0 card-shadow">
      <div className="card-header">
        <Typography variant="h6" className="table-title">
          Invoice History
        </Typography>
      </div>
      <CardContent className="px-0 subscription-invoice">
        <TableServer
          columns={columns}
          data={dataList}
          showSearch={true}
          fetchData={handleFetchData}
          isLoading={isLoading || isValidating}
          pageCount={pageCount}
          totalRecords={totalRecords}
        />
      </CardContent>
    </Card>
  );
}

export default InvoiceHistory;
